import { useEffect, useMemo, useRef, useState } from 'react';
import { FOTO_SOORTEN, type Foto, type FotoSoort, type Registratie } from '@/types/domain';
import { getFotoService } from '@/services/serviceFactory';
import { bytesLeesbaar } from '@/utils/format';
import { verkleinAfbeelding } from '@/utils/afbeelding';
import { Knop } from '@/components/ui/Knop';
import { CameraIcoon, PotloodIcoon, SluitIcoon, VergrootIcoon } from '@/components/layout/Iconen';
import { FotoViewer } from './FotoViewer';
import './form.css';

interface FotoSectieProps {
  registratie: Registratie;
  bijWijziging: (fotos: Foto[]) => void;
  bijFout: (bericht: string) => void;
}

/** Maximale bestandsgrootte per foto; hierboven waarschuwen we de coach. */
const MAX_BYTES = 12 * 1024 * 1024;

export function FotoSectie({ registratie, bijWijziging, bijFout }: FotoSectieProps): JSX.Element {
  const [bezig, setBezig] = useState(false);
  const [soort, setSoort] = useState<FotoSoort>('Voorkant');
  const [urls, setUrls] = useState<Record<string, string>>({});
  // null = geen foto opengeklapt; anders de index in registratie.fotos.
  const [bekekenIndex, setBekekenIndex] = useState<number | null>(null);
  // Id van de foto waarvan de toelichting wordt bewerkt.
  const [bewerktId, setBewerktId] = useState<string | null>(null);
  const [conceptToelichting, setConceptToelichting] = useState('');
  const cameraInvoer = useRef<HTMLInputElement>(null);
  const galerijInvoer = useRef<HTMLInputElement>(null);

  // De opslag is niet openbaar, dus per foto wordt een tijdelijke link
  // opgehaald. Lokale foto's hebben die niet nodig.
  useEffect(() => {
    let actief = true;
    const dienst = getFotoService();

    Promise.all(
      registratie.fotos.map(async (foto) => {
        try {
          return [foto.id, await dienst.weergaveUrl(foto)] as const;
        } catch {
          return [foto.id, ''] as const;
        }
      })
    ).then((paren) => {
      if (actief) setUrls(Object.fromEntries(paren));
    });

    return () => {
      actief = false;
    };
  }, [registratie.fotos]);

  /**
   * Foto's per soort bij elkaar. Na het opslaan wil een coach niet zoeken
   * welke foto van de achterkant was; groeperen scheelt dat.
   */
  const groepen = useMemo(
    () =>
      FOTO_SOORTEN.map((groepSoort) => ({
        soort: groepSoort,
        fotos: registratie.fotos.filter((foto) => foto.soort === groepSoort),
      })).filter((groep) => groep.fotos.length > 0),
    [registratie.fotos]
  );

  const verwerkBestanden = async (bestanden: FileList | null) => {
    if (!bestanden || bestanden.length === 0) return;

    setBezig(true);
    const dienst = getFotoService();
    const nieuwe: Foto[] = [];

    for (const origineel of Array.from(bestanden)) {
      // Eerst verkleinen, dan pas de grens toetsen: een foto van 5 MB uit de
      // telefoon komt na verkleinen ruim onder de limiet uit.
      const bestand = await verkleinAfbeelding(origineel);

      if (bestand.size > MAX_BYTES) {
        bijFout(`${origineel.name} is groter dan ${bytesLeesbaar(MAX_BYTES)} en is overgeslagen.`);
        continue;
      }
      try {
        nieuwe.push(await dienst.upload(registratie, bestand, soort));
      } catch (fout) {
        bijFout(
          fout instanceof Error
            ? `Uploaden van ${bestand.name} mislukte: ${fout.message}`
            : `Uploaden van ${bestand.name} mislukte.`
        );
      }
    }

    if (nieuwe.length > 0) bijWijziging([...registratie.fotos, ...nieuwe]);
    setBezig(false);
  };

  const verwijder = async (foto: Foto) => {
    try {
      await getFotoService().verwijderen(registratie, foto);
    } catch (fout) {
      // De verwijzing halen we hoe dan ook weg; het bestand kan handmatig weg.
      bijFout(
        fout instanceof Error
          ? `Foto verwijderen uit de opslag mislukte: ${fout.message}`
          : 'Foto verwijderen mislukte.'
      );
    }
    bijWijziging(registratie.fotos.filter((f) => f.id !== foto.id));
  };

  const startBewerken = (foto: Foto) => {
    setBewerktId(foto.id);
    setConceptToelichting(foto.toelichting);
  };

  const bewaarToelichting = (foto: Foto) => {
    bijWijziging(
      registratie.fotos.map((f) =>
        f.id === foto.id ? { ...f, toelichting: conceptToelichting.trim() } : f
      )
    );
    setBewerktId(null);
  };

  return (
    <>
      <p className="sectie__toelichting">
        Kies eerst waar de foto van is, en maak hem daarna. Tik op een foto om hem groot te
        bekijken, of op het potlood om erbij te zetten wat erop staat.
      </p>

      <div className="veld">
        <span className="veld__label">Deze foto is van de</span>
        <div className="segment" role="group" aria-label="Soort foto">
          {FOTO_SOORTEN.map((optie) => (
            <button
              key={optie}
              type="button"
              className={`segment__optie${soort === optie ? ' segment__optie--actief' : ''}`}
              aria-pressed={soort === optie}
              onClick={() => setSoort(optie)}
            >
              {optie}
            </button>
          ))}
        </div>
      </div>


      {/*
        Toevoegen staat ónder de foto's, als vlak en niet als knop in een balk.
        Zo hoort het bij de reeks in plaats van bij de bediening eromheen, en
        het is meteen het grootste raakvlak van de sectie.
      */}
      <button
        type="button"
        className="foto-toevoegen"
        disabled={bezig}
        onClick={() => cameraInvoer.current?.click()}
      >
        <CameraIcoon />
        {bezig ? 'Bezig...' : 'Foto maken'}
      </button>

      <Knop variant="subtiel" breed bezig={bezig} onClick={() => galerijInvoer.current?.click()}>
        Of kies er een uit de galerij
      </Knop>

      {groepen.map((groep) => (
        <section key={groep.soort} className="fotogroep">
          <h4 className="fotogroep__kop">
            {groep.soort}
            <span className="fotogroep__aantal">{groep.fotos.length}</span>
          </h4>

          <div className="fotos">
            {groep.fotos.map((foto) => {
              const index = registratie.fotos.indexOf(foto);
              const wordtBewerkt = bewerktId === foto.id;

              return (
                <figure key={foto.id} className={`foto${foto.lokaal ? ' foto--wachtrij' : ''}`}>
                  <div className="foto__beeld">
                    {urls[foto.id] ? (
                      <button
                        type="button"
                        className="foto__open"
                        onClick={() => setBekekenIndex(index)}
                        aria-label={`Foto ${foto.soort} groot bekijken`}
                      >
                        <img
                          src={urls[foto.id]}
                          alt={foto.toelichting || `${foto.soort}: ${foto.bestandsnaam}`}
                          loading="lazy"
                        />
                        <span className="foto__soortcapsule">{foto.soort}</span>
                        <span className="foto__vergroot" aria-hidden="true">
                          <VergrootIcoon />
                        </span>
                      </button>
                    ) : (
                      <div className="foto__laden" aria-hidden="true" />
                    )}

                    <button
                      type="button"
                      className="foto__knop foto__knop--potlood"
                      onClick={() => startBewerken(foto)}
                      aria-label={`Toelichting bij deze foto ${
                        foto.toelichting ? 'aanpassen' : 'toevoegen'
                      }`}
                    >
                      <PotloodIcoon />
                    </button>

                    <button
                      type="button"
                      className="foto__knop foto__knop--verwijder"
                      onClick={() => void verwijder(foto)}
                      aria-label={`Foto ${foto.bestandsnaam} verwijderen`}
                    >
                      <SluitIcoon />
                    </button>
                  </div>

                  <figcaption className="foto__bijschrift">
                    {wordtBewerkt ? (
                      <div className="foto__bewerken">
                        <textarea
                          className="tekstvak foto__tekstvak"
                          rows={2}
                          value={conceptToelichting}
                          placeholder="Wat staat erop?"
                          autoFocus
                          onChange={(event) => setConceptToelichting(event.target.value)}
                        />
                        <div className="foto__bewerkknoppen">
                          <Knop variant="primair" compact onClick={() => bewaarToelichting(foto)}>
                            Gereed
                          </Knop>
                          <Knop variant="subtiel" compact onClick={() => setBewerktId(null)}>
                            Annuleren
                          </Knop>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`foto__toelichting${
                          foto.toelichting ? '' : ' foto__toelichting--leeg'
                        }`}
                        onClick={() => startBewerken(foto)}
                      >
                        {foto.toelichting || 'Toelichting toevoegen'}
                      </button>
                    )}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </section>
      ))}

      {bekekenIndex !== null && (
        <FotoViewer
          fotos={registratie.fotos}
          index={bekekenIndex}
          urls={urls}
          bijSluiten={() => setBekekenIndex(null)}
          bijWisselen={setBekekenIndex}
          bijWijziging={(id, velden) =>
            bijWijziging(
              registratie.fotos.map((f) => (f.id === id ? { ...f, ...velden } : f))
            )
          }
        />
      )}

      {/* capture opent op mobiel direct de achtercamera. */}
      <input
        ref={cameraInvoer}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(event) => {
          void verwerkBestanden(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={galerijInvoer}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => {
          void verwerkBestanden(event.target.files);
          event.target.value = '';
        }}
      />
    </>
  );
}
